/**
 * InquiryLog — Inquiry Agent 运行日志展示组件
 *
 * 源：specs/001-inquiry-card-agent-log/data-model.md §3；tasks T014 / T019。
 *
 * 特性：
 *  - variant='card'   紧凑列表、无分组（列表页卡片下方）
 *  - variant='detail' 按日期分组（ContactProgressPage tab — T019 实现）
 *  - RUNNING 状态文本与图标使用 emerald 绿色（research.md R2）
 *  - 多行 `\n` 保留（`whitespace-pre-line`）
 *  - 占位符 `{key}` 按 displayData 替换（research.md R1）
 *  - 三态：loading / empty / error（i18n）
 *  - IM + REST + fallback 轮询由 useInquiryLogs 统一处理
 */

import { FC, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import useInquiryLogs from './useInquiryLogs';
import { groupLogsByDate, toItemView, formatGroupDate } from './utils';
import type { InquiryLogItemView, InquiryLogProps } from './types';
// import LogItemExtra from './LogItemExtra';
import NoTasksEmptyIcon from '@/features/agent-progress/assets/no-tasks-empty.svg?react';
// ---------------------------------------------------------------------------
// 状态图标
// ---------------------------------------------------------------------------

/**
 * RunningDot — Figma `9458-30189` RUNNING 状态脉冲绿点。
 *
 * 视觉 token（已按像素对齐 Figma）：
 *  - 外容器 16×16，圆形，背景 `#ECFDF5`，0.5px 边框 `#A7F3D0`
 *  - 外层白色描边（Figma box-shadow 0 0 0 2.67px #FFF）
 *  - 内圆 6.67×6.67，`#10B981`，发光阴影 `0 0 5.33px rgba(17,228,158,0.5)`
 *  - `animate-pulse` 提示运行中
 */
// const RUNNING_DOT_OUTER_STYLE: React.CSSProperties = {
//   boxShadow: '0 0 0 2.67px #FFF',
// };

const RUNNING_DOT_INNER_STYLE: React.CSSProperties = {
  boxShadow: '0 0 5.33px 0 rgba(17, 228, 158, 0.5)',
};

const RunningDot: FC<{ className?: string }> = ({ className }) => (
  <span
    className={cn(
      'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[#A7F3D0] bg-[#ECFDF5]',
      className
    )}
    // style={RUNNING_DOT_OUTER_STYLE}
    aria-hidden
  >
    <span
      className="block h-[6.67px] w-[6.67px] animate-pulse rounded-full bg-[#10B981]"
      style={RUNNING_DOT_INNER_STYLE}
    />
  </span>
);

/**
 * StatusIcon — 按 status 渲染 RUNNING / FAILED / 默认（COMPLETED）图标。
 * 使用 16×16 统一尺寸以对齐 Figma `Icon / check`。
 */
const StatusIcon: FC<{ status: string }> = ({ status }) => {
  if (status === 'RUNNING') return <RunningDot />;
  if (status === 'FAILED') {
    return (
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
        <span className="block h-2 w-2 rounded-full bg-[#EF4444]" />
      </span>
    );
  }
  return <Check size={16} className="shrink-0 stroke-[#525252]" aria-hidden />;
};

/**
 * DetailCompletedIcon — detail variant 完成状态图标。
 * Figma `Frame 2147225312` 组件：16×16 白色圆形，1px #D4D4D4 边框，内嵌 10×10 check 图标。
 */
const DetailCompletedIcon: FC = () => (
  <span
    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#D4D4D4] bg-white"
    aria-hidden
  >
    <Check size={10} className="stroke-[#D4D4D4]" strokeWidth={2} />
  </span>
);

// ---------------------------------------------------------------------------
// 单条日志渲染（card variant — 无时间列）
// ---------------------------------------------------------------------------

const LogItemRow: FC<{ item: InquiryLogItemView }> = ({ item }) => {
  const isRunning = item.status === 'RUNNING';
  const isFailed = item.status === 'FAILED';
  return (
    <div className="flex items-center gap-2 text-[12px] leading-[1.5] tracking-[0.015em]">
      {/* 状态图标 */}
      <StatusIcon status={item.status} />

      {/* 文本（保留 \n） */}
      <span
        className={cn('min-w-0 break-words truncate', {
          'text-[#10B981]': isRunning,
          'text-[#EF4444]': isFailed,
          'text-[#525252]': !isRunning && !isFailed,
        })}
      >
        {item.resolvedText}
      </span>

      {/* 右侧特殊展示（由 LogItemExtra 根据 raw 数据自动判断） */}
      {/* <LogItemExtra raw={item.raw} /> */}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 组件主体
// ---------------------------------------------------------------------------

const InquiryLog: FC<InquiryLogProps> = ({
  inquiryId,
  variant = 'card',
  polling = true,
  pollingInterval,
  order = 'asc',
  fallbackMissing = 'keep',
  className,
}) => {
  const { t } = useTranslation();

  const { data, loading, error } = useInquiryLogs(inquiryId, {
    enabled: polling,
    pollingFallbackInterval: pollingInterval,
    disablePollingFallback: !polling,
    order,
  });

  // REST/IM 原始条目 → ViewModel
  const items = useMemo<InquiryLogItemView[]>(() => {
    return (data ?? []).map((raw, idx) =>
      toItemView(raw, idx, {
        fallbackMissing,
        timeMode: 'time',
      })
    );
  }, [data, fallbackMissing]);

  // ------- 状态分支：loading / error / empty -------
  if (loading && items.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center py-16 px-4 py-[10px] text-[12px] text-[#A3A3A3]',
          className
        )}
        role="status"
      >
        {t('inquiries.log.loading')}
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center py-16 px-4 py-[10px] text-[12px] text-[#EF4444]',
          className
        )}
        role="alert"
      >
        {t('inquiries.log.error')}
      </div>
    );
  }

  if (!items.length) {
    if (variant === 'card') return <></>;
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <NoTasksEmptyIcon className="w-[208px] h-[107px] mb-2" />
        <span className="text-xs text-[#737373] tracking-wider text-center">{t('inquiries.log.empty')}</span>
      </div>
    );
  }

  // ------- 内容 -------
  if (variant === 'detail') {
    // detail variant 用 short 时间格式 (HH:mm)
    const detailItems = (data ?? []).map((raw, idx) => toItemView(raw, idx, { fallbackMissing, timeMode: 'short' }));
    const groups = groupLogsByDate(detailItems);
    return (
      <div className={cn('flex flex-col', className)}>
        {groups.map((group, groupIdx) => (
          <section key={group.dateKey} className={cn('flex flex-col', { 'mt-5': groupIdx > 0 })}>
            {/* 分组 header：日期标题 + 横线 */}
            <div className="flex items-center gap-4">
              <span className="shrink-0 text-[14px] leading-[1.5] tracking-[0.005em] text-[#737373]">
                {formatGroupDate(group.dateKey)}
              </span>
              <span className="h-px flex-1 bg-[#E5E5E5]" aria-hidden />
            </div>
            {/* 时间轴 + 日志条目 */}
            <div className="relative pt-3 pl-9">
              {/* 竖线：absolute，从第一个图标中心到最后一个图标中心 */}
              {group.items.length > 1 && (
                <span className="absolute left-[11px] w-px bg-[#E5E5E5]" style={{ top: 23, bottom: 11 }} aria-hidden />
              )}
              <div className="flex flex-col gap-5">
                {group.items.map((item, itemIdx) => {
                  const isRunning = item.status === 'RUNNING';
                  const isFailed = item.status === 'FAILED';
                  const isLast = itemIdx === group.items.length - 1;
                  return (
                    <div key={item.key} className="relative flex items-start">
                      {/* 状态图标：absolute，居中在竖线上 */}
                      <span className="absolute -left-8 top-[3px] flex h-4 w-4 items-center justify-center rounded-full bg-white">
                        {isRunning ? (
                          <RunningDot />
                        ) : isFailed ? (
                          <span className="inline-flex h-4 w-4 items-center justify-center" aria-hidden>
                            <span className="block h-2 w-2 rounded-full bg-[#EF4444]" />
                          </span>
                        ) : (
                          <DetailCompletedIcon />
                        )}
                      </span>
                      {/* 最后一条：用白色遮挡块盖住图标以下多余的竖线 */}
                      {isLast && group.items.length > 1 && (
                        <span className="absolute -left-8 w-4 bg-white" style={{ top: 19, bottom: 0 }} aria-hidden />
                      )}
                      {/* 内容区：step + 描述文本 + 右侧时间 */}
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-4">
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="text-[12px] leading-[1.5] tracking-[0.015em] text-[#525252]">
                            {item.stepLabel}
                          </span>
                          <span
                            className={cn('whitespace-pre-line text-[14px] leading-[1.5] tracking-[0.005em]', {
                              // 'text-[#10B981]': isRunning,
                              // 'text-[#EF4444]': isFailed,
                              // 'text-[#262626]': !isRunning && !isFailed,
                            })}
                          >
                            {item.resolvedText}
                          </span>
                        </div>
                        <span className="shrink-0 text-[12px] leading-[1.5] tracking-[0.015em] text-[#737373]">
                          {item.displayTime}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        ))}
      </div>
    );
  }

  // variant='card' — Figma `9458-30189`: 容器 padding 10px 16px, gap 6px, 无时间列
  // 最大高度 86px，超出滚动，最新消息始终在最下方
  return <CardLogList inquiryId={inquiryId} items={items} className={className} />;
};

// ---------------------------------------------------------------------------
// Card variant 独立组件（处理 max-height + auto-scroll-to-bottom）
// ---------------------------------------------------------------------------

const CardLogList: FC<{
  inquiryId: string;
  items: InquiryLogItemView[];
  className?: string;
}> = ({ inquiryId, items, className }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // 每次 items 变化时自动滚动到底部，保证最新消息可见
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [items]);

  return (
    <div
      ref={scrollRef}
      className={cn('flex max-h-[86px] flex-col gap-[6px] overflow-y-auto px-4 py-[10px]', className)}
    >
      {items.map(item => (
        <LogItemRow key={`${inquiryId}-${item.key}`} item={item} />
      ))}
    </div>
  );
};

export default InquiryLog;
export { InquiryLog };
